import React, { useState, useEffect, useCallback } from "react";
import { User, WhatsAppConversation } from "../types/api";
import { Layout } from "../components/layout/Layout";
import { ConversationList, PhoneGroup } from "../components/whatsapp/ConversationList";
import { ConversationHeader } from "../components/whatsapp/ConversationHeader";
import { MessageList } from "../components/whatsapp/MessageList";
import { MessageInput } from "../components/whatsapp/MessageInput";
import { WhatsAppTemplatePicker } from "../components/whatsapp/WhatsAppTemplatePicker";
import { useWhatsAppMessages } from "../hooks/useWhatsAppMessages";
import { useWhatsAppSSE } from "../hooks/useWhatsAppSSE";
import apiService from "../services/api";
import { normalizePhone } from "../utils/phoneUtils";

type NavigateScreen =
  | "calendar"
  | "patients"
  | "tasks"
  | "internal-tasks"
  | "patient-page"
  | "patient"
  | "users"
  | "connections"
  | "whatsapp";

type StatusFilter = 'all' | 'open' | 'solved' | 'pending';

interface WhatsAppScreenProps {
  user: User;
  users?: User[];
  onLogout: () => void;
  onNavigate?: (screen: NavigateScreen, patientId?: string | number, meetingId?: string | number, phone?: string) => void;
  taskCount?: number;
  whatsappUnreadCount?: number;
  onClearUnreadCount?: () => void;
  phoneNumber?: string;
  patientId?: string | number;
}

export const WhatsAppScreen: React.FC<WhatsAppScreenProps> = ({
  user,
  users,
  onLogout,
  onNavigate,
  taskCount,
  whatsappUnreadCount,
  onClearUnreadCount,
  phoneNumber: initialPhone,
  patientId: initialPatientId,
}) => {
  const token = localStorage.getItem("token") || "";

  const [conversations, setConversations] = useState<WhatsAppConversation[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [selectedPhone, setSelectedPhone] = useState<string | undefined>(undefined);
  const [isSending, setIsSending] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [templateDraft, setTemplateDraft] = useState<{ body: string; templateId: string } | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // Determine if we're in single-conversation mode (opened from patient table/page)
  const isSingleMode = !!(initialPhone || initialPatientId);

  // Derive aggregated group data for the selected phone (includes assign/status from most recent conv)
  const selectedGroup: PhoneGroup | null = selectedPhone
    ? (() => {
        const convs = conversations.filter((c) => normalizePhone(c.phone_number) === selectedPhone);
        if (convs.length === 0) {
          return {
            phone_number: selectedPhone,
            unread_count: 0,
            can_send_freeform: false,
          };
        }
        const latest = convs.reduce((a, b) =>
          !a.last_message_at || (b.last_message_at && b.last_message_at > a.last_message_at) ? b : a
        );
        return {
          phone_number: selectedPhone,
          patient_name: convs.find((c) => c.patient_name)?.patient_name,
          patient_hm_id: convs.find((c) => c.patient_hm_id)?.patient_hm_id,
          patient_id: convs.find((c) => c.patient_id)?.patient_id,
          unread_count: convs.reduce((sum, c) => sum + (c.unread_count || 0), 0),
          last_message_at: latest.last_message_at,
          last_message_preview: latest.last_message_preview,
          can_send_freeform: convs.some((c) => c.can_send_freeform),
          assigned_user_id: latest.assigned_user_id,
          assigned_user_name: latest.assigned_user_name,
          status: latest.status,
        };
      })()
    : null;

  // Messages hook keyed by phone
  const {
    messages,
    isLoading: isLoadingMessages,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    addMessage,
    addOptimisticMessage,
  } = useWhatsAppMessages(selectedPhone, token);

  const selectedPhoneRef = React.useRef<string | undefined>(undefined);

  useWhatsAppSSE(selectedPhone, true, {
    onNewMessage: (msg) => {
      addMessage(msg);
      if (selectedPhone) {
        apiService.markWhatsAppRead({ token, phone_number: selectedPhone }).catch(() => {});
        onClearUnreadCount?.();
      }
      setConversations((prev) =>
        prev.map((c) =>
          c.phone_number === selectedPhone
            ? {
                ...c,
                last_message_at: msg.created_at,
                last_message_preview: msg.body,
                unread_count: 0,
                // Patient replied — re-enable freeform input immediately
                ...(msg.direction === "inbound" ? { can_send_freeform: true } : {}),
              }
            : c
        )
      );
    },
    onConversationStatusChanged: (status) => {
      setConversations((prev) =>
        prev.map((c) =>
          c.phone_number === selectedPhone
            ? { ...c, status: status as any }
            : c
        )
      );
    },
  });

  const loadConversations = useCallback(async (search?: string, filter?: StatusFilter) => {
    setIsLoadingConversations(true);
    const activeFilter = filter !== undefined ? filter : statusFilter;
    try {
      const res = await apiService.getWhatsAppConversations({
        token,
        search,
        status: activeFilter !== 'all' ? activeFilter : undefined,
      });
      const openPhone = selectedPhoneRef.current;
      setConversations(
        openPhone
          ? res.conversations.map((c) =>
              c.phone_number === openPhone ? { ...c, unread_count: 0 } : c
            )
          : res.conversations
      );
      return res.conversations;
    } catch (e) {
      console.error("[WhatsApp] Failed to load conversations", e);
      return [];
    } finally {
      setIsLoadingConversations(false);
    }
  }, [token, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    selectedPhoneRef.current = selectedPhone;
  }, [selectedPhone]);

  const prevUnreadRef = React.useRef(whatsappUnreadCount);
  useEffect(() => {
    if (whatsappUnreadCount !== prevUnreadRef.current) {
      prevUnreadRef.current = whatsappUnreadCount;
      loadConversations();
    }
  }, [whatsappUnreadCount, loadConversations]);

  useEffect(() => {
    loadConversations().then((convs) => {
      if (initialPhone) {
        setSelectedPhone(normalizePhone(initialPhone));
      } else if (initialPatientId) {
        const pid = Number(initialPatientId);
        const found = convs.find((c) => c.patient_id === pid);
        if (found) setSelectedPhone(normalizePhone(found.phone_number));
      }
    });
  }, [initialPhone, initialPatientId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedPhone) return;
    apiService.markWhatsAppRead({ token, phone_number: selectedPhone }).catch(() => {});
    onClearUnreadCount?.();
    setConversations((prev) =>
      prev.map((c) => normalizePhone(c.phone_number) === selectedPhone ? { ...c, unread_count: 0 } : c)
    );
  }, [selectedPhone]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStatusFilterChange = (filter: StatusFilter) => {
    setStatusFilter(filter);
    loadConversations(undefined, filter);
  };

  const handleSelectGroup = (group: PhoneGroup) => {
    const phone = normalizePhone(group.phone_number);
    setSelectedPhone(phone);
    const params = new URLSearchParams();
    params.set("phone", phone);
    if (group.patient_id) params.set("patient_id", group.patient_id.toString());
    window.history.replaceState({}, "", `/whatsapp?${params.toString()}`);
  };

  const handleBack = () => {
    setSelectedPhone(undefined);
    window.history.replaceState({}, "", "/whatsapp");
  };

  const handleSend = async (text: string, mediaUrl?: string, templateId?: string) => {
    if (!selectedPhone) return;
    setIsSending(true);
    if (text) addOptimisticMessage(text, selectedPhone, !!templateId);
    try {
      await apiService.sendWhatsAppMessage({
        token,
        phone_number: selectedPhone,
        patient_id: selectedGroup?.patient_id,
        body: text,
        media_url: mediaUrl,
        template_id: templateId,
      });
      loadConversations();
    } catch (e) {
      console.error("[WhatsApp] Failed to send message", e);
    } finally {
      setIsSending(false);
    }
  };

  const handleAssign = async (userId: number | undefined, userName: string | undefined) => {
    if (!selectedPhone) return;
    try {
      const res = await apiService.assignWhatsAppConversation({
        token,
        phone_number: selectedPhone,
        user_id: userId,
        user_name: userName,
      });
      setConversations((prev) =>
        prev.map((c) =>
          c.phone_number === selectedPhone
            ? { ...c, assigned_user_id: res.conversation.assigned_user_id, assigned_user_name: res.conversation.assigned_user_name }
            : c
        )
      );
    } catch (e) {
      console.error("[WhatsApp] Failed to assign conversation", e);
    }
  };

  const handleConversationStatusChange = async (status: 'open' | 'solved' | 'pending') => {
    if (!selectedPhone) return;
    try {
      await apiService.updateWhatsAppConversationStatus({
        token,
        phone_number: selectedPhone,
        status,
      });
      // Use the requested status directly — API response may be null
      setConversations((prev) =>
        prev.map((c) =>
          c.phone_number === selectedPhone
            ? { ...c, status }
            : c
        )
      );
    } catch (e) {
      console.error("[WhatsApp] Failed to update conversation status", e);
    }
  };

  const handleTemplateSelect = (body: string, templateId: string) => {
    setTemplateDraft({ body, templateId });
    setShowTemplatePicker(false);
  };

  useEffect(() => {
    if (templateDraft) {
      handleSend(templateDraft.body, undefined, templateDraft.templateId);
      setTemplateDraft(null);
    }
  }, [templateDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNavigateToPatient = (patientId: number) => {
    onNavigate?.("patient", patientId);
  };

  // Build the conversation object to pass to ConversationHeader
  const headerConversation: WhatsAppConversation = selectedGroup
    ? {
        id: conversations.find((c) => c.phone_number === selectedGroup.phone_number)?.id ?? 0,
        phone_number: selectedGroup.phone_number,
        patient_id: selectedGroup.patient_id,
        patient_name: selectedGroup.patient_name,
        patient_hm_id: selectedGroup.patient_hm_id,
        unread_count: 0,
        can_send_freeform: selectedGroup.can_send_freeform,
        assigned_user_id: selectedGroup.assigned_user_id,
        assigned_user_name: selectedGroup.assigned_user_name,
        status: selectedGroup.status,
      }
    : { id: 0, phone_number: "", unread_count: 0, can_send_freeform: false };

  const chatPanel = selectedGroup ? (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", height: "100%" }}>
      <ConversationHeader
        conversation={headerConversation}
        showBackButton={true}
        onBack={handleBack}
        onNavigateToPatient={handleNavigateToPatient}
        users={users}
        onAssign={handleAssign}
        onStatusChange={handleConversationStatusChange}
      />
      {isLoadingMessages ? (
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#e5ddd5",
            color: "#888",
          }}
        >
          טוען הודעות...
        </div>
      ) : (
        <MessageList
          messages={messages}
          currentUserId={typeof user.id === "number" ? user.id : Number(user.id)}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onLoadMore={fetchNextPage}
        />
      )}
      <MessageInput
        onSend={(text, mediaUrl) => handleSend(text, mediaUrl)}
        onTemplateClick={() => setShowTemplatePicker(true)}
        disabled={isSending}
        canSendFreeform={selectedGroup.can_send_freeform}
      />
    </div>
  ) : (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f5f5",
        color: "#888",
        gap: 12,
      }}
    >
      <div style={{ fontSize: 48 }}>💬</div>
      <div style={{ fontSize: 16, fontWeight: 500 }}>בחר שיחה כדי להתחיל</div>
      <div style={{ fontSize: 13 }}>כל השיחות מוצגות בלוח השמאלי</div>
    </div>
  );

  const showList = !isSingleMode || !selectedPhone;

  return (
    <Layout
      user={user}
      onLogout={onLogout}
      onNavigate={onNavigate as any}
      activeMenuItem="whatsapp"
      taskCount={taskCount}
    >
      <div
        style={{
          display: "flex",
          height: "calc(100vh - 60px)",
          overflow: "hidden",
        }}
      >
        {showList && (
          <ConversationList
            conversations={conversations}
            selectedPhone={selectedPhone}
            isLoading={isLoadingConversations}
            onSelect={handleSelectGroup}
            onSearch={(q) => loadConversations(q)}
            onStatusFilter={handleStatusFilterChange}
            activeStatusFilter={statusFilter}
          />
        )}

        {chatPanel}
      </div>

      {showTemplatePicker && (
        <WhatsAppTemplatePicker
          onSelect={handleTemplateSelect}
          onClose={() => setShowTemplatePicker(false)}
          patient={selectedGroup?.patient_name ? {
            full_name: selectedGroup.patient_name,
            first_name: selectedGroup.patient_name.split(" ")[0],
            last_name: selectedGroup.patient_name.split(" ").slice(1).join(" "),
            hm_id: selectedGroup.patient_hm_id,
            phone: selectedGroup.phone_number,
          } : undefined}
        />
      )}
    </Layout>
  );
};
